import { Field, Input, Label } from '@headlessui/react'
import clsx from 'clsx'
import { ChangeHandler, RefCallBack } from 'react-hook-form'
import { BaseTooltip } from '../Tooltip/Tooltip'
import { InputHTMLAttributes } from 'react'

type BaseInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  ref?: RefCallBack
  name?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: ChangeHandler
  size?: 'sm' | 'md'
  className?: string
  label?: string
  placeholder?: string
  error?: string
  type?: string
  disabled?: boolean
  readOnly?: boolean
  variant?: 'default' | 'secondary'
  rightText?: string|React.ReactNode
  leftText?: string|React.ReactNode
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  toolpitText?: string
  optionInputClasses?: string
}

export const BaseInput = (props: BaseInputProps) => {
  const {
    ref,
    name,
    onChange,
    onBlur,
    className,
    label = '',
    size = 'sm',
    placeholder,
    error,
    type = 'text',
    value,
    disabled,
    readOnly, // Добавлено новое свойство
    variant = 'default',
    rightText,
    leftText,
    leftIcon,
    rightIcon,
    toolpitText,
    optionInputClasses,
  } = props

  const baseClasses =
    'relative block w-full disabled:cursor-not-allowed disabled:opacity-75 focus:outline-none border-0 placeholder-text-secondary ring ring-card-border dark:ring-card-border-dark focus:ring-2 focus:ring-card-border dark:focus:ring-card-border-dark'
  const iconPaddingLeft = leftIcon || leftText ? (variant === 'default' ? 'pl-10' : 'pl-12') : ''
  const iconPaddingRight = rightIcon
    ? variant === 'default'
      ? 'pr-10'
      : 'pr-12'
    : rightText
      ? 'pr-14'
      : ''
  const variantClasses = size === 'sm' ? 'rounded-lg px-3 py-2' : 'rounded-2xl px-4 py-3'
  const inputClasses = clsx(
    baseClasses,
    variantClasses,
    iconPaddingLeft,
    iconPaddingRight,
    optionInputClasses,
    {
      'ring-error focus:ring-error dark:ring-error dark:focus:ring-error': !!error,
      'text-text-main dark:text-white bg-light-bg dark:bg-card-background disabled:text-text-main dark:disabled:text-white disabled:bg-light-purple dark:disabled:bg-dark-purple':
        variant === 'default',
      'text-text-secondary bg-light-bg dark:bg-card-background disabled:bg-light-purple dark:disabled:bg-dark-purple':
        variant !== 'default',
      'cursor-default': readOnly, // Добавлен класс для readOnly полей
    }
  )

  return (
    <Field className={`flex flex-col gap-2 relative ${className || ''}`}>
      {label && (
        <div className="flex items-center gap-2">
          <Label
            className={
              variant === 'default' ? 'text-text-secondary' : 'text-text-main dark:text-white'
            }
          >
            {label}
          </Label>
          {toolpitText && <BaseTooltip text={toolpitText} />}
        </div>
      )}
      <div className="relative w-full">
        {leftText && (
          <span className="top-5 left-3 z-1 absolute text-text-secondary -translate-y-1/2 transform">
            {leftText}
          </span>
        )}
        {leftIcon && (
          <span className="top-[50%] left-3 z-1 absolute -translate-y-1/2 transform">
            {leftIcon}
          </span>
        )}
        <Input
          ref={ref}
          name={name}
          type={type}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          className={inputClasses}
        />
        {rightIcon && (
          <span className="top-5 right-3 absolute -translate-y-1/2 transform">{rightIcon}</span>
        )}
        {rightText && (
          <span className="top-5 right-3 absolute text-primary dark:text-primary-dark uppercase -translate-y-1/2 transform">
            {rightText}
          </span>
        )}
        {error && (
          <div className="mt-0.5 text-[10px] text-error md:text-xs text-right">{error}</div>
        )}
      </div>
    </Field>
  )
}
